/**
 * SecureAuthService - Authentication service following FAQ requirements
 * Implements secure user management with proper validation and RLS
 */

import { supabase } from '../config/supabase';
import userService from './UserService';

class SecureAuthService {
  constructor() {
    this.currentUser = null;
    this.listeners = [];
    this.isInitialized = false;
    
    // Initialize auth state
    this.initialize();
  }

  // =============================================
  // INITIALIZATION & AUTH STATE
  // =============================================

  async initialize() {
    try {
      // Get current session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session:', error);
        return;
      }

      if (session?.user) {
        await this.loadUserProfile(session.user.id);
      }

      // Listen for auth changes
      supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('Auth state changed:', event);
        
        switch (event) {
          case 'SIGNED_IN':
            // User signed in, load profile
            if (session?.user) {
              await this.loadUserProfile(session.user.id);
            }
            break;
          case 'SIGNED_OUT':
            // User signed out, clear data
            this.clearCurrentUser();
            break;
          case 'TOKEN_REFRESHED':
            // Session refreshed, user data should remain the same
            break;
          case 'INITIAL_SESSION':
            // Initial session found, load profile if user exists
            if (session?.user) {
              await this.loadUserProfile(session.user.id);
            }
            break;
          default:
            // Handle any other auth events
            console.log('Unhandled auth event:', event);
            break;
        }
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing auth service:', error);
    }
  }

  // =============================================
  // AUTHENTICATION METHODS
  // =============================================

  /**
   * Register new user with validation
   */
  async register(userData) {
    try {
      // Validate required fields
      const requiredFields = ['email', 'password', 'username', 'user_type', 'city', 'state'];
      for (const field of requiredFields) {
        if (!userData[field]) {
          throw new Error(`Missing required field: ${field}`);
        }
      }

      // Validate email format
      if (!this.isValidEmail(userData.email)) {
        throw new Error('Invalid email format');
      }

      // Validate password strength
      if (!this.isValidPassword(userData.password)) {
        throw new Error('Password must be at least 6 characters long');
      }

      // Validate username
      if (!this.isValidUsername(userData.username)) {
        throw new Error('Username must be 3-50 characters, alphanumeric and underscores only');
      }

      // Validate user type
      if (!['fan', 'band', 'venue'].includes(userData.user_type)) {
        throw new Error('Invalid user type');
      }

      // Sanitize input
      const sanitizedData = {
        email: userData.email.toLowerCase().trim(),
        password: userData.password,
        username: userData.username.trim().replace(/[^a-zA-Z0-9_]/g, ''),
        user_type: userData.user_type,
        city: userData.city.trim(),
        state: userData.state.trim(),
        latitude: userData.latitude,
        longitude: userData.longitude
      };

      // Register with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: sanitizedData.email,
        password: sanitizedData.password,
        options: {
          data: {
            username: sanitizedData.username,
            user_type: sanitizedData.user_type && sanitizedData.user_type.toLowerCase(),
            city: sanitizedData.city,
            state: sanitizedData.state,
            latitude: sanitizedData.latitude,
            longitude: sanitizedData.longitude
          }
        }
      });

      if (authError) {
        throw authError;
      }

      if (!authData.user) {
        throw new Error('Registration failed - no user returned');
      }

      // Do NOT create user profile in database here. The trigger will handle it.
      return {
        success: true,
        user: null,
        needsEmailConfirmation: !authData.session
      };

    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.message || 'Registration failed'
      };
    }
  }

  /**
   * Login user with security checks
   */
  async login(email, password) {
    try {
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      if (!this.isValidEmail(email)) {
        throw new Error('Invalid email format');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password: password
      });

      if (error) {
        // Map Supabase errors to user-friendly messages
        switch (error.message) {
          case 'Invalid login credentials':
            throw new Error('Invalid email or password');
          case 'Email not confirmed':
            throw new Error('Please check your email and click the confirmation link');
          default:
            throw error;
        }
      }

      if (!data.user) {
        throw new Error('Login failed - no user returned');
      }

      // Load user profile
      const profileResult = await this.loadUserProfile(data.user.id);
      
      if (!profileResult.success) {
        throw new Error('Failed to load user profile');
      }

      return {
        success: true,
        user: this.currentUser
      };

    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  }

  /**
   * Logout user
   */
  async logout() {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }

      this.clearCurrentUser();
      return { success: true };

    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        error: error.message || 'Logout failed'
      };
    }
  }

  /**
   * Reset password
   */
  async resetPassword(email) {
    try {
      if (!email || !this.isValidEmail(email)) {
        throw new Error('Valid email address is required');
      }

      const { error } = await supabase.auth.resetPasswordForEmail(
        email.toLowerCase().trim(),
        {
          redirectTo: `${window.location.origin}/reset-password`
        }
      );

      if (error) {
        throw error;
      }

      return {
        success: true,
        message: 'Password reset email sent'
      };

    } catch (error) {
      console.error('Password reset error:', error);
      return {
        success: false,
        error: error.message || 'Password reset failed'
      };
    }
  }

  /**
   * Update password
   */
  async updatePassword(newPassword) {
    try {
      if (!this.isValidPassword(newPassword)) {
        throw new Error('Password must be at least 6 characters long');
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw error;
      }

      return {
        success: true,
        message: 'Password updated successfully'
      };

    } catch (error) {
      console.error('Password update error:', error);
      return {
        success: false,
        error: error.message || 'Password update failed'
      };
    }
  }

  // =============================================
  // USER PROFILE MANAGEMENT
  // =============================================

  /**
   * Load user profile from database
   */
  async loadUserProfile(userId) {
    try {
      const result = await userService.loadUserProfile(userId);
      
      if (result.success) {
        this.currentUser = result.user;
        userService.currentUser = result.user;
        this.notifyListeners();
      }

      return result;

    } catch (error) {
      console.error('Error loading user profile:', error);
      return {
        success: false,
        error: error.message || 'Failed to load user profile'
      };
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(updates) {
    try {
      if (!this.currentUser) {
        throw new Error('No user logged in');
      }

      const result = await userService.updateUserProfile(this.currentUser.id, updates);
      
      if (result.success) {
        this.currentUser = result.user;
        this.notifyListeners();
      }

      return result;

    } catch (error) {
      console.error('Error updating profile:', error);
      return {
        success: false,
        error: error.message || 'Failed to update profile'
      };
    }
  }

  // =============================================
  // VALIDATION METHODS
  // =============================================

  isValidEmail(email) {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
  }

  isValidPassword(password) {
    return password && password.length >= 6;
  }

  isValidUsername(username) {
    if (!username || username.length < 3 || username.length > 50) {
      return false;
    }
    // Only alphanumeric and underscores
    return /^[a-zA-Z0-9_]+$/.test(username);
  }

  // =============================================
  // GETTERS & STATE MANAGEMENT
  // =============================================

  getCurrentUser() {
    return this.currentUser;
  }

  isLoggedIn() {
    return this.currentUser !== null;
  }

  isAdmin() {
    return this.currentUser && this.currentUser.is_admin === true;
  }

  canModerate() {
    return this.currentUser && (
      this.currentUser.is_admin === true || 
      this.currentUser.scene_points >= 100
    );
  }

  isApprovedAdvertiser() {
    return this.currentUser && this.currentUser.is_approved_advertiser === true;
  }

  getUserLevel() {
    if (!this.currentUser) return null;
    return userService.getUserLevel(this.currentUser.scene_points);
  }

  clearCurrentUser() {
    this.currentUser = null;
    userService.clearCurrentUser();
    this.notifyListeners();
  }

  // =============================================
  // EVENT LISTENERS
  // =============================================

  addListener(callback) {
    this.listeners.push(callback);
    return callback;
  }

  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.currentUser);
      } catch (error) {
        console.error('Error in auth listener:', error);
      }
    });
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  /**
   * Get current session
   */
  async getSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        throw error;
      }

      return { success: true, session };

    } catch (error) {
      console.error('Error getting session:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Refresh session
   */
  async refreshSession() {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        throw error;
      }

      return { success: true, session };

    } catch (error) {
      console.error('Error refreshing session:', error);
      return { success: false, error: error.message };
    }
  }
}

export default SecureAuthService; 